/**
 * Sentry za web panel. Isključen dok `VITE_SENTRY_DSN` nije postavljen: SDK se tada ni ne učitava (dinamički
 * import), nema mrežnih poziva, a sve funkcije ispod su bez efekta. Šalju se samo ID-jevi (tenant, korisnik,
 * uloga), nikad ime, e-pošta ili IP; `beforeSend` dodatno čisti tokene i e-poštu (vidi `scrub.ts`).
 */
import type * as SentryNS from '@sentry/react';
import { ApiClientError, isApiClientError, newRequestId, type Me } from '@sm/api';
import type { QueryClient } from '@tanstack/react-query';

import { env } from '@/env';

import { scrubBreadcrumb, scrubEvent, scrubUrl } from '@sm/core/scrub';

type Sentry = typeof SentryNS;
let sentry: Sentry | null = null;

/** Verzija i build iz Vite `define` (vidi vite.config.ts). */
export const APP_VERSION: string = __SM_APP_VERSION__;
export const APP_BUILD: string = __SM_BUILD_ID__;
export const RELEASE: string = __SM_RELEASE__;

export function sentryEnabled(): boolean {
  return sentry !== null;
}

/** Inicijalizacija pri pokretanju (main.tsx). Bez DSN-a ne radi ništa. */
export async function initObservability(): Promise<void> {
  if (!env.sentryDsn || sentry) return;
  const S = await import('@sentry/react');
  S.init({
    dsn: env.sentryDsn,
    environment: env.sentryEnvironment,
    release: RELEASE,
    sendDefaultPii: false,
    integrations: [S.browserTracingIntegration()],
    tracesSampleRate: env.sentryTracesSampleRate,
    // Tracing zaglavlja (sentry-trace, baggage) samo prema našem API-ju.
    tracePropagationTargets: [env.apiUrl],
    beforeSend: (event) => scrubEvent(event),
    beforeSendTransaction: (event) => scrubEvent(event),
    beforeBreadcrumb: (b) => scrubBreadcrumb(b),
    initialScope: { tags: { client: 'web', app_build: APP_BUILD } },
  });
  sentry = S;
}

/** Tenant, korisnik i uloga (samo ID-jevi) nakon `/me`; `null` pri odjavi. */
export function setUserContext(me: Pick<Me, 'user' | 'tenant' | 'role'> | null): void {
  if (!sentry) return;
  if (!me) {
    sentry.setUser(null);
    sentry.setTag('tenant_id', undefined);
    sentry.setTag('role', undefined);
    return;
  }
  sentry.setUser({ id: me.user.id });
  sentry.setTag('tenant_id', me.tenant.id);
  sentry.setTag('role', me.role);
}

/** Prati `me` u kešu upita (prijava, odjava, promjena firme). */
export function bindUserContext(qc: QueryClient): () => void {
  return qc.getQueryCache().subscribe((e) => {
    if (e.query.queryKey[0] !== 'me') return;
    if (e.type === 'removed') setUserContext(null);
    else if (e.type === 'updated') setUserContext((e.query.state.data as Me | undefined) ?? null);
  });
}

/** Da li grešku poslati u Sentry: API samo mreža i 5xx; ostale (JS) greške uvijek. */
export function isReportable(err: unknown): boolean {
  if (isApiClientError(err)) return err.isReportable;
  return err instanceof Error;
}

interface CaptureCtx {
  source: 'query' | 'mutation' | 'boundary' | 'router';
  key?: string;
  /** Šifra greške koja je prikazana korisniku (ako nije API greška). */
  errorId?: string;
  componentStack?: string;
}

/** Šalje grešku sa request ID-jem kao tagom (podrška traži po „Šifri greške“). */
export function captureError(err: unknown, ctx: CaptureCtx): void {
  if (!sentry || !isReportable(err)) return;
  const S = sentry;
  S.withScope((scope) => {
    scope.setTag('source', ctx.source);
    if (ctx.key) scope.setTag('query_key', scrubUrl(ctx.key));
    if (isApiClientError(err)) {
      scope.setTag('http.status_code', String(err.status));
      scope.setTag('api_error_code', err.network ? 'network_error' : err.code);
      if (err.requestId) scope.setTag('request_id', err.requestId);
      scope.setFingerprint(['api', err.network ? 'network' : String(err.status), ctx.key ?? '']);
    } else if (ctx.errorId) {
      scope.setTag('request_id', ctx.errorId);
    }
    if (ctx.componentStack) scope.setContext('react', { componentStack: ctx.componentStack });
    S.captureException(err);
  });
}

function keyOf(k: readonly unknown[] | undefined): string {
  return k ? JSON.stringify(k).slice(0, 120) : '';
}

/** onError za QueryCache (TanStack Query): 5xx i mreža, ne 4xx validacija. */
export function onQueryError(err: unknown, q: { queryKey: readonly unknown[] }): void {
  captureError(err, { source: 'query', key: keyOf(q.queryKey) });
}

/** onError za MutationCache. */
export function onMutationError(
  err: unknown,
  _variables: unknown,
  _onMutateResult: unknown,
  m: { options: { mutationKey?: readonly unknown[] } },
): void {
  captureError(err, { source: 'mutation', key: keyOf(m.options.mutationKey) });
}

/** Šifra greške za ekran pada aplikacije: request ID API greške ili novi ID (tag `request_id` u Sentryju). */
export function errorIdFor(err: unknown): string {
  return isApiClientError(err) && err.requestId ? err.requestId : newRequestId();
}

export { ApiClientError };
